/**
 * Knowledge Configuration Hooks
 *
 * React Query hooks for tenant-level knowledge defaults:
 * - Chunking strategy & parameters
 * - Enrichment flags (LLM enrichment, question extraction, auto-classify)
 * - Embedding model selection
 *
 * API: GET/PUT /{tenant_id}/config/knowledge
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import apiClient from '@/lib/api-client';

// =============================================================================
// Types
// =============================================================================

export type ChunkingStrategy = 'semantic_heuristic' | 'semantic_llm' | 'paragraph';

export interface KnowledgeChunkingDefaults {
  strategy: ChunkingStrategy;
  min_tokens: number;
  max_tokens: number;
  target_tokens: number;
  overlap_tokens: number;
}

export interface KnowledgeEnrichmentDefaults {
  enrich_with_llm: boolean;
  extract_questions: boolean;
  auto_classify: boolean;
}

export interface KnowledgeEmbeddingDefaults {
  model_id: string | null;
  /** Read-only effective resolution surfaced by GET /config/knowledge. */
  effective_model_id?: string | null;
  effective_display_name?: string | null;
  effective_provider?: string | null;
  effective_dimensions?: number | null;
}

export interface KnowledgeDefaultsConfig {
  chunking: KnowledgeChunkingDefaults;
  enrichment: KnowledgeEnrichmentDefaults;
  embedding: KnowledgeEmbeddingDefaults;
}

// =============================================================================
// Query Keys
// =============================================================================

export const knowledgeConfigKeys = {
  all: (tenantId: string) => ['knowledgeConfig', tenantId] as const,
};

// =============================================================================
// API Functions
// =============================================================================

async function fetchKnowledgeConfig(): Promise<KnowledgeDefaultsConfig> {
  // NOTE: Don't include tenants/{id} in path — apiClient.buildUrl() adds it automatically
  const response = await apiClient.get<KnowledgeDefaultsConfig>(
    '/api/v1/config/knowledge'
  );
  return response.data;
}

async function updateKnowledgeConfig(
  data: KnowledgeDefaultsConfig
): Promise<KnowledgeDefaultsConfig> {
  // NOTE: Don't include tenants/{id} in path — apiClient.buildUrl() adds it automatically
  const response = await apiClient.put<KnowledgeDefaultsConfig>(
    '/api/v1/config/knowledge',
    data
  );
  return response.data;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch tenant knowledge defaults.
 */
export function useKnowledgeConfig() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: knowledgeConfigKeys.all(tenantId ?? ''),
    queryFn: () => fetchKnowledgeConfig(),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/**
 * Update tenant knowledge defaults.
 */
export function useUpdateKnowledgeConfig() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: KnowledgeDefaultsConfig) =>
      updateKnowledgeConfig(data),
    onSuccess: (data) => {
      queryClient.setQueryData(knowledgeConfigKeys.all(tenantId!), data);
    },
  });
}
